/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DoCheck, Inject, Input, NgZone, OnChanges, SimpleChanges} from '@angular/core';
import template from './folder-analysis-page.component.html';
import {VisitorProfileResource} from '../../components/api/resources/resource/visitor-profile';

@Component({
  standalone: false,
  selector: 'adn-folder-analysis-page',
  template
})
export class FolderAnalysisPageComponent implements DoCheck, OnChanges {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;

  effectiveIsNew = false;

  activeTab: 'info' | 'profiles' | 'charts' = 'info';
  browserId = '';
  profile: any = {};
  folderScript = '';

  private lastFolderId: string | undefined;
  private subscribedModelComms: any;

  constructor(
    @Inject(VisitorProfileResource) private readonly visitorProfileResource: any,
    private ngZone: NgZone
  ) {
  }

  get showNewMessage(): boolean {
    return this.effectiveIsNew || !this.model?.folderId;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (changes.modelComms) {
      this.subscribeModelComms();
    }
  }

  ngDoCheck(): void {
    const folderId = this.model?.folderId;
    if (folderId !== this.lastFolderId) {
      this.lastFolderId = folderId;
      this.folderScript = folderId ? this.buildFolderScript(folderId) : '';
    }
  }

  selectTab(tab: 'info' | 'profiles' | 'charts'): void {
    this.activeTab = tab;
  }

  runRetrieve(): void {
    this.profile = {};
    if (!this.browserId) {
      this.profile.hasError = true;
      this.profile.errorInfo = 'Specify a browser ID to retrieve a user profile.';
      return;
    }
    this.visitorProfileResource.getVisitor(this.model.folderId, this.browserId, (data: any) => {
      this.ngZone.run(() => {
        this.profile.results = {...data};
        delete this.profile.results.id;
      });
    }, (err: any) => {
      this.ngZone.run(() => {
        if (err.status === 404) {
          this.profile.errorInfo = 'Unable to retrieve a profile for ' + this.browserId;
        } else {
          this.profile.errorInfo = err.info;
        }
      });
    }, this.profile);
  }

  private subscribeModelComms(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }
    this.subscribedModelComms = this.modelComms;
    this.modelComms.addSubscriber((data: any, type: any, isNewFromComms: boolean) => {
      if (type) {
        return;
      }
      this.ngZone.run(() => {
        this.effectiveIsNew = isNewFromComms;
      });
    });
  }

  private buildFolderScript(folderId: string): string {
    return `<script src="https://cdn.adnuntius.com/adn.js" async></script>
<script>
    window.adn = window.adn || {};
    adn.calls = adn.calls || [];
    adn.calls.push(function() {
        adn.view('${folderId}');
    });
</script>`;
  }
}
